import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { FulfillmentStatus } from "common/types/fulfillments";
import { toDateTime } from "common/utils/dates";
import { TrackerStatus } from "common/types/shipping";
import { OrderStatus, OrderItemStatus } from "common/types/orders";
import LinkButton from "common/components/LinkButton";
import Icon from "common/components/Icon";

import Timeline, { TimelinePropEventOpts } from "client/common/components/content/Timeline";
import { ModalStatus } from "client/common/types";
import { urls } from "client/routes";

import { ClientListOrder, ClientListOrderItem } from "../types";

const placedEvent = ({ order }: OrderItemTimelineProps): TimelinePropEventOpts => {
  return {
    key: "placed",
    header: "Order placed",
    content: toDateTime(order.order_placed_time),
    status: "complete",
  };
};

const approvalEvent = ({
  orderItem,
  needsControlledConfirmation,
}: OrderItemTimelineProps): TimelinePropEventOpts => {
  const getOrderItemApprovalTimelineContent = () => {
    if (!orderItem.was_approval_needed) {
      return "No approval needed";
    }
    if (!orderItem.is_approved) {
      return needsControlledConfirmation ? "Pending veterinary confirmation" : "Pending approval";
    }
    // At this point we know the order item is approved
    if (orderItem.prescription_fill?.is_approved) {
      return `Approved by ${orderItem.prescription_fill.prescribed_by_name}${
        orderItem.prescription_fill.approved_at &&
        ` on ${toDateTime(orderItem.prescription_fill.approved_at)}`
      }`;
    }
    return "Approved";
  };

  const getOrderItemApprovalTimelineStatus = () => {
    return !orderItem.was_approval_needed || orderItem.is_approved ? "complete" : "active";
  };

  return {
    key: "approval",
    header: "Veterinary approval",
    content: getOrderItemApprovalTimelineContent(),
    status: getOrderItemApprovalTimelineStatus(),
  };
};

const fulfillmentEvent = ({ order, orderItem }: OrderItemTimelineProps): TimelinePropEventOpts => {
  const fulfillmentToStatus: Record<FulfillmentStatus, string> = {
    [FulfillmentStatus.Completed]: "Ready for pickup",
    [FulfillmentStatus.ReadyForPickup]: "Ready for pickup",
    [FulfillmentStatus.ReadyToPick]: "Preparing item",
    [FulfillmentStatus.ReadyToReview]: "Preparing item",
    [FulfillmentStatus.ReadyToShip]: "Preparing item",
    [FulfillmentStatus.Canceled]: "Canceled",
    [FulfillmentStatus.MissedPickup]: "Pending",
  };

  const getOrderItemFulfillmentTimelineHeader = () => {
    if (orderItem.is_pickup) {
      return "Preparing pickup";
    }
    if (orderItem.tracker) {
      return "Shipped";
    }
    return "Preparing shipment";
  };

  const getOrderItemFulfillmentTimelineContent = () => {
    if (orderItem.is_canceled) {
      return "Canceled";
    }
    if (orderItem.tracker) {
      return (
        <LinkButton
          dest={urls.orders.detail({
            params: { id: order.id },
            search: { trackingModalIsVisible: ModalStatus.Open },
          })}
        >
          {orderItem.tracker.status_display}: Track via {orderItem.tracker.carrier}{" "}
          <Icon icon={faArrowRight} />
        </LinkButton>
      );
    }
    if (orderItem.fulfillment_status && orderItem.is_pickup) {
      return fulfillmentToStatus[orderItem.fulfillment_status];
    }
    return "Pending";
  };

  const getOrderItemFulfillmentTimelineStatus = () => {
    if (!orderItem.fulfillment_status && !orderItem.tracker) {
      return "inactive";
    }
    if (orderItem.is_pickup && orderItem.fulfillment_status) {
      return orderItem.fulfillment_status === FulfillmentStatus.Completed ? "complete" : "active";
    }
    if (!orderItem.is_pickup && orderItem.tracker) {
      return orderItem.tracker.status === TrackerStatus.Delivered ? "complete" : "active";
    }
    return "inactive";
  };

  return {
    key: "fulfillment",
    header: getOrderItemFulfillmentTimelineHeader(),
    content: getOrderItemFulfillmentTimelineContent(),
    status: getOrderItemFulfillmentTimelineStatus(),
  };
};

const deliveredEvent = ({ order, orderItem }: OrderItemTimelineProps): TimelinePropEventOpts => {
  const getOrderItemDeliveredTimelineHeader = () => {
    if (orderItem.is_canceled) {
      return "Canceled";
    }
    return orderItem.is_pickup ? "Picked up" : "Delivered";
  };

  const getOrderItemDeliveredTimelineContent = () => {
    if (orderItem.status === OrderItemStatus.Delivered) {
      if (orderItem.is_pickup) {
        return "Order was picked up!";
      }
      if (orderItem.tracker?.last_updated) {
        return `Order was delivered on ${toDateTime(orderItem.tracker.last_updated)}`;
      }
      return "Order was delivered";
    }
    return orderItem.is_canceled ? "Canceled" : "Pending";
  };

  const getOrderItemDeliveredTimelineStatus = () => {
    if (order.status === OrderStatus.PaymentFailed || orderItem.is_canceled) {
      return "error";
    }
    if (orderItem.status === OrderItemStatus.Delivered) {
      return "complete";
    }
    return "inactive";
  };

  return {
    key: "delivered",
    header: getOrderItemDeliveredTimelineHeader(),
    content: getOrderItemDeliveredTimelineContent(),
    status: getOrderItemDeliveredTimelineStatus(),
  };
};

type OrderItemTimelineProps = {
  order: ClientListOrder;
  orderItem: ClientListOrderItem;
  needsControlledConfirmation: boolean;
};

const OrderItemTimeline = (props: OrderItemTimelineProps) => {
  const { orderItem } = props;
  return (
    <Timeline
      size="s"
      events={[
        placedEvent(props),
        approvalEvent(props),
        ...(!orderItem.is_canceled ? [fulfillmentEvent(props)] : []),
        deliveredEvent(props),
      ]}
    />
  );
};

export default OrderItemTimeline;
